import React, { useEffect, useState } from "react";
import moment from "moment";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import InputTextarea from "../../../components/InputTextarea";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { getToken } from "../../../services/auth";
import { getSubdomain } from "../../../helpers/domain";

// Limite da SEFAZ: 20 cartas por nota, e vale sempre a última — ela
// SUBSTITUI as anteriores em vez de somar. Quem não sabe disso escreve só a
// correção nova e perde a anterior.
const MAX_CORRECTIONS = 20;

/**
 * Carta de correção eletrônica (CC-e) — só NF-e (BDM-887).
 *
 * Compartilhada pela lista e pela aba do pedido. O texto deixa explícito o
 * que a CC-e NÃO corrige: é aí que o usuário erra e leva rejeição.
 */
export const ModalCorrectDocument = ({ document, close, onDone }) => {
    const [letter, setLetter] = useState('');
    const [processing, setProcessing] = useState(false);
    // Cartas já enviadas nesta nota. A lista não carrega eventos (seria peso
    // morto em toda linha), então busca aqui — é aqui que a pergunta "o que
    // já foi corrigido?" aparece.
    const [corrections, setCorrections] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    useEffect(() => {
        if (!document) return;

        setLetter('');
        setCorrections([]);
        setLoadingHistory(true);

        api.get(`/fiscal-document/${document.id}`).then(res => {
            setCorrections(
                (res.data.resource?.events ?? [])
                    .filter(event => event.type === 'correction')
                    .sort((a, b) => (b.sequence ?? 0) - (a.sequence ?? 0))
            );
        }).catch(() => {}).then(() => setLoadingHistory(false));
    }, [document]);

    const openCceFile = format => {
        window.open(`${process.env.REACT_APP_URL}/fiscal-document/${document.id}/cce/${format}?_token=${getToken()}&_subdomain=${getSubdomain()}`);
    };

    const statusLabel = status => ({
        concluded: 'Registrada na SEFAZ',
        rejected: 'Recusada',
        processing: 'Processando',
    }[status] ?? status);

    const correct = () => {
        setProcessing(true);

        api.post(`/fiscal-document/${document.id}/correct`, { letter }).then(() => {
            onDone('Carta de correção enviada.');
            close();
        }).catch(renderError).then(() => setProcessing(false));
    };

    // O Modal alterna classe em vez de desmontar (tem transição de saída),
    // então ele fica sempre no ar e o conteúdo é que se protege do null.
    return (
        <Modal
            show={document !== null}
            close={close}
            title={`Carta de correção`}
            footer={
                <Button
                    type={`primary`}
                    size={`small`}
                    text={`Enviar correção`}
                    action={correct}
                    loading={processing}
                    disabled={letter.trim().length < 15}
                />
            }
        >
            <div className="section">
                <p>
                    A carta de correção é enviada à SEFAZ e passa a acompanhar a nota. O que
                    ela pode corrigir é definição da sua contabilidade.
                    {document?.number ? ` Nota nº ${document.number}.` : ''}
                </p>

                {/* O histórico vem primeiro: sem ele, o usuário reescreve
                    a correção sem saber o que já valeu — e como a última
                    substitui as anteriores, isso apaga o que estava certo. */}
                {corrections.length > 0 &&
                    <div className={`fiscal-corrections`}>
                        <strong className={`corrections-title`}>
                            {corrections.length === 1 ? 'Carta já enviada' : `${corrections.length} cartas já enviadas`}
                        </strong>

                        {corrections.map(correction => (
                            <div className={`correction ${correction.status}`} key={correction.id}>
                                <div className={`correction-head`}>
                                    <span className={`correction-status`}>{statusLabel(correction.status)}</span>
                                    {correction.sequence && <span className={`correction-sequence`}>nº {correction.sequence}</span>}
                                    <span className={`correction-date`}>
                                        {moment(correction.created_at).format('DD/MM/YYYY HH:mm')}
                                    </span>
                                </div>
                                <p className={`correction-content`}>{correction.content}</p>
                                {correction.response_message &&
                                    <small className={`correction-response`}>{correction.response_message}</small>
                                }
                            </div>
                        ))}

                        {corrections.some(correction => correction.status === 'concluded') &&
                            <div className={`correction-files`}>
                                <Button type={`secondary`} size={`small`} text={`PDF da correção`} action={() => openCceFile('pdf')} />
                                <Button type={`secondary`} size={`small`} text={`XML da correção`} action={() => openCceFile('xml')} />
                            </div>
                        }
                    </div>
                }

                {loadingHistory && <p className={`fiscal-hint`}>Carregando as correções anteriores...</p>}

                <InputTextarea
                    label={corrections.length > 0 ? `Texto da nova correção` : `O que precisa ser corrigido?`}
                    value={letter}
                    change={setLetter}
                    rows={3}
                />
                <p className={`fiscal-hint`}>
                    Mínimo de 15 caracteres. {letter.trim().length}/15
                </p>
                <p className={`fiscal-hint`}>
                    Cada nota aceita até {MAX_CORRECTIONS} cartas, e <strong>vale sempre a última</strong>:
                    escreva o texto completo da correção, não só o que mudou desde a anterior.
                </p>
            </div>
        </Modal>
    );
};

export default ModalCorrectDocument;
